import React, { useState } from 'react';
import { Modal, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { translate } from '../../../services/translate';
import { useForm } from 'react-hook-form';
import FilterCheckBox from '../../../components/Filter/components/FilterCheckBox';
import FilterNumericRange from '../../../components/Filter/components/FilterNumericRange';
import MyDateRangePicker from '../../../components/MyDateRangePicker';
import moment from 'moment';
import PolygonTypeDropdown from '../../../components/PolygonTypeDropdown';
import PolygonByTypeDropdown from '../../../components/PolygonByTypeDropdown';
import FormActions from '../../../components/FormActions';
import { crmService } from '../../../services/central-api/crm';
import Toast from 'react-native-toast-message';
import CompanyDropdown from '../../../components/CompanyDropdown';

interface Props {
    isVisible: boolean;
    setIsVisible: React.Dispatch<React.SetStateAction<boolean>>;
}
type ValuePiece = Date | null;

interface DownloadForm {
    companyId: number;
    start: { from: ValuePiece; to: ValuePiece; };
    finish: { from: ValuePiece; to: ValuePiece; };
    onlyOffline: boolean;
    fromDuration: number;
    toDuration: number;
}

export default function CameraHistoryReportModal({ isVisible, setIsVisible }: Props) {
    const { styles } = useStyles(stylesheet);
    const [selectedPolygonType, setSelectedPolygonType] = useState<string>('');
    const [polygonPendingLoading, setPolygonPendingLoading] = useState<boolean>(false);
    const [selectedPolygon, setSelectedPolygon] = useState<number>(0);

    const defaultEmptyValues: DownloadForm = {
        companyId: 0,
        start: { from: null, to: null },
        finish: { from: null, to: null },
        onlyOffline: false,
        fromDuration: 0,
        toDuration: 0,
    };

    const form = useForm<DownloadForm>({
        defaultValues: defaultEmptyValues,
        mode: 'onChange'
    });

    function onCloseModal() {
        form.reset(defaultEmptyValues);
        setIsVisible(false);
    }

    async function formSubmit() {
        try {
            const values = form.getValues();
            const csv = await crmService.downloadReportOfflineHistory({
                ...values,
                fromStart: values.start.from ? values.start.from.getTime() : undefined,
                toStart: values.start.to ? values.start.to.getTime() : undefined,
                fromFinish: values.finish.from ? values.finish.from.getTime() : undefined,
                toFinish: values.finish.to ? values.finish.to.getTime() : undefined,
                polygonType: selectedPolygonType !== '' ? selectedPolygonType : undefined,
                polygon: selectedPolygon !== 0 ? selectedPolygon : undefined
            });
            if (!(csv instanceof Blob)) {
                throw new Error();
            }
            const url = window.URL.createObjectURL(csv);
            const a = document.createElement('a');
            a.href = url;
            a.download = `offline-tickets-${moment(new Date()).format('YYYY-MM-DD')}.csv`;
            a.click();
            window.URL.revokeObjectURL(url);

            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });

            onCloseModal();
        } catch (error) {
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    return (
        <Modal
            visible={isVisible}
            animationType='fade'
            transparent
            onRequestClose={onCloseModal}
        >
            <View style={styles.modalContainer}>
                <View style={styles.modalComponent}>
                    <CompanyDropdown
                        form={form}
                        fieldName='companyId'
                        required={true}
                        disabled={false}
                        zIndex={7}
                    />

                    <MyDateRangePicker
                        label={translate('startBetween')}
                        maxDate={moment().toDate()}
                        form={form}
                        fieldName='start'
                        zIndex={5}
                    />
                    <MyDateRangePicker
                        label={translate('finishBetween')}
                        maxDate={moment().toDate()}
                        form={form}
                        fieldName='finish'
                        zIndex={4}
                    />

                    <FilterNumericRange
                        label={`${translate('duration')}(${translate('minutes')})`}
                        onFromChange={(value) => {
                            form.setValue('fromDuration', value ?? 0);
                        }}
                        fromValue={form.watch('fromDuration')}
                        onToChange={(value) => {
                            form.setValue('toDuration', value ?? 0);
                        }}
                        toValue={form.watch('toDuration')}
                        hideClearValue={true}
                    />

                    <FilterCheckBox
                        label={translate('onlyOpenedTickets')}
                        value={form.watch('onlyOffline')}
                        onChange={(value) => {
                            form.setValue('onlyOffline', value);
                        }}
                    />
                    <PolygonTypeDropdown
                        label={translate('regions')}
                        dropDownDirection='BOTTOM'
                        showTooltip
                        tooltipText={translate('regionsInfo')}
                        zIndex={3}
                        polygonType={selectedPolygonType}
                        setPolygonType={setSelectedPolygonType}
                        requiresCompanyId={true}
                        companyId={form.getValues().companyId}
                    />
                    {selectedPolygonType != '' &&
                        <PolygonByTypeDropdown
                            label={translate('subRegions')}
                            dropDownDirection='BOTTOM'
                            setPolygon={setSelectedPolygon}
                            polygon={selectedPolygon}
                            polygonType={selectedPolygonType}
                            showTooltip
                            tooltipText={translate('subRegionsInfo')}
                            zIndex={2}
                            setPendingLoading={setPolygonPendingLoading}
                            requiresCompanyId={true}
                            companyId={form.getValues().companyId}
                        />
                    }
                    <FormActions
                        onSubmit={formSubmit}
                        onClose={() => onCloseModal()}
                        disabled={!form.formState.isValid || polygonPendingLoading}
                        confirmText={'download'} />
                </View>
            </View>
        </Modal>
    );
}

const stylesheet = createStyleSheet(theme => ({
    modalContainer: {
        flex: 1,
        backgroundColor: '#31313199',
        justifyContent: 'center',
        alignItems: 'center',
    },

    fieldsView: {
        gap: 15,
    },

    modalComponent: {
        minWidth: {
            xs: undefined,
            md: 450
        },
        minHeight: 150,
        width: {
            md: undefined,
            xs: '90%'
        },
        backgroundColor: theme.colors.container.background,
        borderRadius: 4,
        padding: 20,
        rowGap: 25
    },

    modalTitle: {
        fontSize: 18,
        fontWeight: '600'
    },
    sideBarItemTitle: {
        fontSize: 14,
        color: theme.colors.neutral[500],
        fontWeight: '600',
    },
    sideBarItemContent: {
        fontSize: 14,
        color: theme.colors.neutral[400],
    },
}));
